import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Alert, Button, Input, Result } from 'antd'

import { getCookie, removeCookie } from 'src/common/cookies'
import config from 'src/common/config'
import api from 'src/common/api'

import Loader from 'src/components/Loader'
import QRCodePng from 'src/components/QRCodePng'
import expiredQR from '../../assets/img/qrscaduto.png'

const Home = props => {
  const { intl } = props
  const [isLoading, setIsLoading] = useState(false)

  const [token] = useState(getCookie('token'))
  const [expiry] = useState(() => {
    const v = Number(getCookie('expiry'))
    return Number.isFinite(v) ? v : 0
  })

  const getRemaining = useCallback(
    () => Math.max(0, expiry - Math.floor(Date.now() / 1000)),
    [expiry]
  )

  const formatTimer = secs => {
    const m = Math.floor(secs / 60)
    const s = secs - 60 * m
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  const [expirySeconds, setExpirySeconds] = useState(getRemaining)
  const [timer, setTimer] = useState(() => formatTimer(getRemaining()))

  const [pingStatus, setPingStatus] = useState('OK')
  const [needLDAP, setNeedLDAP] = useState(true)
  const [smsStatus, setSmsStatus] = useState(0)
  const [smsMessage, setSmsMessage] = useState('')
  // Held in state, not read off the DOM on submit: setIsLoading swaps the whole
  // tree for the loader, which unmounts the input, so an uncontrolled field comes
  // back empty and the user has to retype the username after every error.
  const [ldapValue, setLdapValue] = useState('')

  const checkPingStatus = async () => {
    try {
      const res = await api.ping.checkStatus()
      return res.status
    } catch (error) {
      return error.response?.status || 500
    }
  }

  useEffect(() => {
    const authCookie = getCookie('token')

    const checkStatus = async () => {
      setIsLoading(true)
      const status = await checkPingStatus()
      if (status === 200) {
        window.location.href = config.LOGIN_URL
      } else {
        setPingStatus('KO')
        setIsLoading(false)
      }
    }

    if (!authCookie) {
      checkStatus()
    }
  }, [])

  useEffect(() => {
    if (getRemaining() <= 0) {
      if (getCookie('token') || getCookie('expiry')) {
        removeCookie('token', { path: '/', domain: config.FE_DOMAIN })
        removeCookie('expiry', { path: '/', domain: config.FE_DOMAIN })
      }
      return
    }
    const id = setInterval(() => {
      const remaining = getRemaining()
      setExpirySeconds(remaining)
      setTimer(formatTimer(remaining))
      if (remaining <= 0) {
        clearInterval(id)
        removeCookie('token', { path: '/', domain: config.FE_DOMAIN })
        removeCookie('expiry', { path: '/', domain: config.FE_DOMAIN })
      }
    }, 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshQR = useCallback(() => {
    removeCookie('token', { path: '/', domain: config.FE_DOMAIN })
    removeCookie('expiry', { path: '/', domain: config.FE_DOMAIN })
    window.location.reload()
  }, [])

  const submitLDAP = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await api.user.sendSms(ldapValue)
      setSmsStatus(res.status)
      setSmsMessage(res.data.message)
      // Only on success. This used to run unconditionally, so any error replaced
      // the form with a dead end: a typo in the username forced a page reload.
      setNeedLDAP(false)
    } catch (error) {
      setSmsStatus(error.response?.status || 500)
      // axios puts the response body in .data, so reading .message directly was
      // always undefined and every message the backend sent was discarded in
      // favour of the generic one. src/common/utils.js already uses this path.
      setSmsMessage(
        error.response?.data?.message ||
        intl.formatMessage({ id: 'error.title.generic' })
      )
    }
    setIsLoading(false)
  }, [intl, ldapValue])

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <h1>
        <FormattedMessage id={'app.title'} />
      </h1>
      {pingStatus === 'OK' ? (
        <>
          {token && expirySeconds > 0 ? (
            <QRCodePng value={token} size={320} id={'qr'} />
          ) : (
            <img src={expiredQR} alt={'expired qr'} width={320} height={320} />
          )}
          <h2>{timer}</h2>
          <Button type={'primary'} size={'large'} onClick={refreshQR}>
            <FormattedMessage id={'button.refresh'} />
          </Button>
        </>
      ) : needLDAP ? (
        <>
          <Input
            id={'ldap'}
            size={'large'}
            placeholder={intl.formatMessage({ id: 'placeholder.ldap' })}
            style={{ width: 320 }}
            value={ldapValue}
            onChange={e => setLdapValue(e.target.value)}
          />
          <Button type={'primary'} size={'large'} onClick={submitLDAP}>
            <FormattedMessage id={'button.submit'} />
          </Button>
          {/* The error is shown next to the field that produced it, so the user can
              correct the username and retry without reloading the page. */}
          {smsMessage && (
            <Alert
              type={smsStatus === 429 ? 'warning' : 'error'}
              message={smsMessage}
              showIcon
              style={{ width: 320 }}
            />
          )}
        </>
      ) : (
        // The backend message only carries the detail ("il link scade entro 5
        // minuti"), which on its own never told the user an SMS was coming at
        // all. The title and the explanation live here so they are localised.
        <Result
          status={'success'}
          title={intl.formatMessage({ id: 'success.title.sms' })}
          subTitle={
            <>
              <FormattedMessage id={'success.description.sms'} />
              {smsMessage && <div>{smsMessage}</div>}
            </>
          }
        />
      )}
    </>
  )
}

export default injectIntl(Home)